import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Table, Tag, Button, Typography, Image, Tabs } from 'antd';
import { orderService } from '../services/orderService';
import { Order } from '../types/order';
import dayjs from 'dayjs';

const { Text } = Typography;

interface OrdersModalProps {
    visible: boolean;
    userId: string;
    onClose: () => void;
}

const OrdersModal: React.FC<OrdersModalProps> = ({ visible, userId, onClose }) => {
    const [orders, setOrders] = useState<Order[]>([]);
    const [purchasedItems, setPurchasedItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [purchasedItemsLoading, setPurchasedItemsLoading] = useState(false);

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const userOrders = await orderService.getOrdersByUser(userId);
            setOrders(userOrders);
        } catch (error) {
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const getPurchasedItems = useCallback(async () => {
        try {
            setPurchasedItemsLoading(true);
            
            const allOrders = await orderService.getAllOrders();
            const userOrders = allOrders.filter(order =>
                order.userId === userId &&
                order.status === 'paid'
            );

            const purchasedItemsData = userOrders.flatMap(order => order.items);

            // Create a map using a composite key of type and id
            const uniqueItemsMap = new Map();

            purchasedItemsData.forEach((item: any) => {
                const key = item.type === 'coffee' ?
                    `coffee_${item.coffeeId || item.id}` :
                    item.type === 'bottled_drink' ?
                    `drink_${item.drinkId || item.id}` :
                    `dish_${item.dishId || item.id}`;

                if (!uniqueItemsMap.has(key)) {
                    uniqueItemsMap.set(key, {
                        ...item,
                        totalQuantity: 0,
                        totalSpent: 0,
                        purchaseCount: 0
                    });
                }

                const existingItem = uniqueItemsMap.get(key);
                existingItem.totalQuantity += item.quantity;
                existingItem.totalSpent += item.price * item.quantity;
                existingItem.purchaseCount += 1;
            });

            const uniqueItems = Array.from(uniqueItemsMap.values());
            console.log('uniqueItems', uniqueItems);

            setPurchasedItems(uniqueItems);
        } catch (error) {
            console.error('Error fetching purchased items:', error);
        } finally {
            setPurchasedItemsLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (visible && userId) {
            fetchOrders();
            getPurchasedItems();
        }
    }, [visible, userId, fetchOrders, getPurchasedItems]);

    const getStatusColor = (status: Order['status']) => {
        switch (status) {
            case 'waiting': return 'orange';
            case 'confirmed': return 'blue';
            case 'shipping': return 'cyan';
            case 'delivered': return 'green';
            case 'paid': return 'success';
            case 'cancelled': return 'red';
            default: return 'default';
        }
    };

    const getStatusText = (status: Order['status']) => {
        switch (status) {
            case 'waiting': return 'Chờ xác nhận';
            case 'confirmed': return 'Đã xác nhận';
            case 'shipping': return 'Đang giao';
            case 'delivered': return 'Đã giao';
            case 'paid': return 'Đã thanh toán';
            case 'cancelled': return 'Đã hủy';
            default: return status;
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '';
        // Kiểm tra nếu date là Firestore Timestamp
        if (date.seconds) {
            return dayjs(new Date(date.seconds * 1000)).format('DD/MM/YYYY HH:mm');
        }
        // Nếu là Date object hoặc string
        return dayjs(date).format('DD/MM/YYYY HH:mm');
    };

    // Columns cho bảng đơn hàng
    const orderColumns = [
        {
            title: 'Mã đơn hàng',
            dataIndex: 'id',
            key: 'id',
            render: (id: string) => (
                <Text strong style={{ fontSize: '12px' }}>
                    #{id.slice(-8).toUpperCase()}
                </Text>
            ),
        },
        {
            title: 'Sản phẩm',
            dataIndex: 'items',
            key: 'items',
            render: (items: Order['items']) => (
                <div style={{ maxWidth: '300px' }}>
                    {items.map((item, index) => (
                        <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {item.imageUrl && (
                                <Image
                                    src={item.imageUrl}
                                    alt={item.name}
                                    width={40}
                                    height={40}
                                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                                    preview={false}
                                />
                            )}
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                    {item.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    Số lượng: {item.quantity}
                                    {item.weight && ` • ${item.weight}g`}
                                    {item.volume && ` • ${item.volume}ml`}
                                    {item.grindType && ` • ${item.grindType === 'whole' ? 'Nguyên hạt' : 'Xay'}`}
                                </div>
                                <div style={{ fontSize: '11px', color: '#1890ff', fontWeight: 'bold' }}>
                                    {item.price.toLocaleString()}đ
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            title: 'Tổng tiền',
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount: number) => (
                <Text strong style={{ color: '#f5222d' }}>
                    {amount.toLocaleString()}đ
                </Text>
            ),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            render: (status: Order['status']) => (
                <Tag color={getStatusColor(status)}>
                    {getStatusText(status)}
                </Tag>
            ),
        },
        {
            title: 'Ngày đặt',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: any) => (
                <div style={{ fontSize: '12px' }}>
                    {formatDate(date)}
                </div>
            ),
        },
        {
            title: 'Thông tin giao hàng',
            dataIndex: 'shippingInfo',
            key: 'shippingInfo',
            render: (shipping: Order['shippingInfo']) => (
                <div style={{ fontSize: '11px', maxWidth: '200px' }}>
                    <div><strong>Tên:</strong> {shipping.fullName}</div>
                    <div><strong>SĐT:</strong> {shipping.phone}</div>
                    <div><strong>Địa chỉ:</strong> {shipping.address}</div>
                    <div>{shipping.ward}, {shipping.district}, {shipping.province}</div>
                </div>
            ),
        },
    ];

    // Columns cho bảng sản phẩm đã mua
    const purchasedItemsColumns = [
        {
            title: 'Sản phẩm',
            dataIndex: 'name',
            key: 'name',
            render: (name: string, record: any) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {record.imageUrl && (
                        <Image
                            src={record.imageUrl}
                            alt={name}
                            width={50}
                            height={50}
                            style={{ objectFit: 'cover', borderRadius: '4px' }}
                            preview={false}
                        />
                    )}
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {record.weight && `${record.weight}g`}
                            {record.volume && `${record.volume}ml`}
                            {record.grindType && ` • ${record.grindType === 'whole' ? 'Nguyên hạt' : 'Xay'}`}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Loại',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => {
                const getTypeInfo = (type: string) => {
                    switch (type) {
                        case 'coffee': return { text: 'Cà phê', color: 'brown' };
                        case 'bottled_drink': return { text: 'Đồ uống', color: 'blue' };
                        case 'dish': return { text: 'Món ăn', color: 'green' };
                        default: return { text: type, color: 'default' };
                    }
                };
                const typeInfo = getTypeInfo(type);
                return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
            },
        },
        {
            title: 'Tổng số lượng đã mua',
            dataIndex: 'totalQuantity',
            key: 'totalQuantity',
            render: (quantity: number) => (
                <Text strong>{quantity}</Text>
            ),
        },
        {
            title: 'Số lần mua',
            dataIndex: 'purchaseCount',
            key: 'purchaseCount',
            render: (count: number) => (
                <Tag color="blue">{count} lần</Tag>
            ),
        },
        {
            title: 'Tổng tiền đã chi',
            dataIndex: 'totalSpent',
            key: 'totalSpent',
            render: (amount: number) => (
                <Text strong style={{ color: '#f5222d' }}>
                    {amount.toLocaleString()}đ
                </Text>
            ),
        },
        {
            title: 'Giá trung bình',
            key: 'averagePrice',
            render: (record: any) => {
                const avgPrice = record.totalSpent / record.totalQuantity;
                return (
                    <Text style={{ color: '#1890ff' }}>
                        {avgPrice.toLocaleString()}đ
                    </Text>
                );
            },
        },
    ];

    const tabItems = [
        {
            key: 'orders',
            label: `Đơn hàng (${orders.length})`,
            children: (
                <Table
                    columns={orderColumns}
                    dataSource={orders}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 1000 }}
                    pagination={{
                        defaultPageSize: 5,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} đơn hàng`,
                    }}
                    summary={(pageData) => {
                        const totalAmount = pageData.reduce((sum, order) => sum + order.totalAmount, 0);
                        const totalOrders = pageData.length;
                        
                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                <Table.Summary.Cell index={0} colSpan={2}>
                                    <Text strong>Tổng cộng ({totalOrders} đơn hàng)</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}>
                                    <Text strong style={{ color: '#f5222d' }}>
                                        {totalAmount.toLocaleString()}đ
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} colSpan={3} />
                            </Table.Summary.Row>
                        );
                    }}
                />
            ),
        },
        {
            key: 'purchased-items',
            label: `Sản phẩm đã mua (${purchasedItems.length})`,
            children: (
                <Table
                    columns={purchasedItemsColumns}
                    dataSource={purchasedItems}
                    loading={purchasedItemsLoading}
                    rowKey={(record) => `${record.type}_${record.coffeeId || record.drinkId || record.dishId || record.id}`}
                    scroll={{ x: 1000 }}
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} của ${total} sản phẩm`,
                    }}
                    summary={(pageData) => {
                        const totalSpent = pageData.reduce((sum, item) => sum + item.totalSpent, 0);
                        const totalQuantity = pageData.reduce((sum, item) => sum + item.totalQuantity, 0);
                        const totalItems = pageData.length;
                        
                        return (
                            <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                <Table.Summary.Cell index={0}>
                                    <Text strong>Tổng cộng ({totalItems} sản phẩm)</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1} />
                                <Table.Summary.Cell index={2}>
                                    <Text strong>{totalQuantity}</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={3} />
                                <Table.Summary.Cell index={4}>
                                    <Text strong style={{ color: '#f5222d' }}>
                                        {totalSpent.toLocaleString()}đ
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5} />
                            </Table.Summary.Row>
                        );
                    }}
                />
            ),
        },
    ];

    return (
        <Modal
            title="Lịch sử mua hàng"
            visible={visible}
            onCancel={onClose}
            footer={[
                <Button key="close" onClick={onClose}>
                    Đóng
                </Button>
            ]}
            width={1200}
            style={{ top: 20 }}
        >
            <Tabs 
                defaultActiveKey="orders" 
                items={tabItems}
                size="large"
            />
        </Modal>
    );
};

export default OrdersModal;
